import { Controller } from "@hotwired/stimulus"
import { editorStore, isoLocal } from "../editor/store"
import { getSunPosition, toUtcDate, estimateUtcOffsetHours } from "../editor/sun"

// UI for insolation controls: date picker, hour slider, quick dates, play.
// Also surfaces the plot's UTC offset (auto-estimated from longitude) with
// +/- override so users can correct for China/India/Spain/DST cases.
export default class extends Controller {
  static targets = [
    "dateInput", "hourSlider", "hourLabel",
    "altitude", "azimuth", "playBtn", "warning",
    "utcOffset", "utcSource",
    "modeShadows", "modeHeatmap", "modeOff",
  ]

  connect() {
    this.#syncFromStore()
    const onSun = () => this.#syncFromStore()
    const onPlot = () => { this.#syncWarning(); this.#syncFromStore() }
    const onMode = () => this.#syncModeButtons()
    editorStore.addEventListener("sun:change", onSun)
    editorStore.addEventListener("plot:change", onPlot)
    editorStore.addEventListener("insolation:mode-change", onMode)
    this._unsubscribe = () => {
      editorStore.removeEventListener("sun:change", onSun)
      editorStore.removeEventListener("plot:change", onPlot)
      editorStore.removeEventListener("insolation:mode-change", onMode)
    }
    this.#syncWarning()
    this.#syncModeButtons()
  }

  disconnect() {
    this.#stopAnimation()
    this._unsubscribe && this._unsubscribe()
  }

  dateChanged() {
    const val = this.dateInputTarget.value
    if (!val) return
    editorStore.setSunDateIso(val.length === 16 ? `${val}:00` : val)
  }

  hourChanged() {
    const hour = parseFloat(this.hourSliderTarget.value)
    const d = new Date(editorStore.sun.dateIso)
    const h = Math.floor(hour)
    const m = Math.round((hour - h) * 60)
    d.setHours(h, m, 0, 0)
    editorStore.setSunDateIso(isoLocal(d))
  }

  setJune()      { this.#setDate(6, 22) }
  setSeptember() { this.#setDate(9, 22) }
  setDecember()  { this.#setDate(12, 22) }
  setNow() {
    const d = new Date()
    editorStore.setSunDateIso(isoLocal(d))
  }

  #setDate(month, day) {
    const year = new Date(editorStore.sun.dateIso).getFullYear() || new Date().getFullYear()
    const d = new Date(year, month - 1, day, 12, 0, 0, 0)
    editorStore.setSunDateIso(isoLocal(d))
  }

  modeShadows() { editorStore.setInsolationMode("shadows") }
  modeHeatmap() { editorStore.setInsolationMode("heatmap") }
  modeOff()     { editorStore.setInsolationMode("off") }

  #syncModeButtons() {
    const mode = editorStore.insolationMode
    if (this.hasModeShadowsTarget) this.modeShadowsTarget.setAttribute("aria-pressed", mode === "shadows")
    if (this.hasModeHeatmapTarget) this.modeHeatmapTarget.setAttribute("aria-pressed", mode === "heatmap")
    if (this.hasModeOffTarget)     this.modeOffTarget.setAttribute("aria-pressed",     mode === "off")
  }

  bumpUtc(step) {
    const current = this.#effectiveOffset()
    editorStore.setUtcOffsetHours(current + step)
  }

  utcPlus() { this.bumpUtc(1) }
  utcMinus() { this.bumpUtc(-1) }
  utcReset() { editorStore.setUtcOffsetHours(null) }

  play() {
    if (this._animRaf) { this.#stopAnimation(); return }
    const base = new Date(editorStore.sun.dateIso)
    base.setHours(0, 0, 0, 0)
    const startTs = performance.now()
    const DURATION_MS = 10_000
    const step = (now) => {
      const t = Math.min(1, (now - startTs) / DURATION_MS)
      const minutes = Math.round(t * (24 * 60 - 1))
      const d = new Date(base.getTime() + minutes * 60_000)
      editorStore.setSunDateIso(isoLocal(d))
      if (t < 1 && this._animRaf != null) {
        this._animRaf = requestAnimationFrame(step)
      } else {
        this.#stopAnimation()
      }
    }
    this._animRaf = requestAnimationFrame(step)
    if (this.hasPlayBtnTarget) this.playBtnTarget.textContent = "⏸ Стоп"
  }

  #stopAnimation() {
    if (this._animRaf) cancelAnimationFrame(this._animRaf)
    this._animRaf = null
    if (this.hasPlayBtnTarget) this.playBtnTarget.textContent = "▶ Проиграть сутки"
  }

  #syncFromStore() {
    const d = new Date(editorStore.sun.dateIso)
    if (this.hasDateInputTarget) this.dateInputTarget.value = editorStore.sun.dateIso.slice(0, 16)
    const hourDec = d.getHours() + d.getMinutes() / 60
    if (this.hasHourSliderTarget) this.hourSliderTarget.value = hourDec.toFixed(2)
    if (this.hasHourLabelTarget) {
      this.hourLabelTarget.textContent = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
    }
    this.#syncUtc()
    this.#syncSunReadout()
  }

  #syncUtc() {
    const off = this.#effectiveOffset()
    const hasOrigin = !!editorStore.plot?.originLngLat
    const override = editorStore.sun.utcOffsetHoursOverride
    const sign = off >= 0 ? "+" : "−"
    if (this.hasUtcOffsetTarget) {
      // No plot coordinates and no manual override — we literally don't
      // know where we are, so claiming "UTC+0" would be wrong.
      this.utcOffsetTarget.textContent =
        hasOrigin || override != null ? `UTC${sign}${Math.abs(off)}` : "—"
    }
    if (this.hasUtcSourceTarget) {
      let label
      if (override != null) label = "вручную"
      else if (hasOrigin) label = "по долготе"
      else label = "нет координат"
      this.utcSourceTarget.textContent = label
    }
  }

  #effectiveOffset() {
    const override = editorStore.sun.utcOffsetHoursOverride
    if (override != null) return override
    const origin = editorStore.plot?.originLngLat
    return origin ? estimateUtcOffsetHours(origin[0]) : 0
  }

  #syncSunReadout() {
    const lat = editorStore.plot?.originLngLat?.[1]
    const lng = editorStore.plot?.originLngLat?.[0]
    if (lat == null || lng == null) {
      if (this.hasAltitudeTarget) this.altitudeTarget.textContent = "—"
      if (this.hasAzimuthTarget) this.azimuthTarget.textContent = "—"
      return
    }
    const utc = toUtcDate(editorStore.sun.dateIso, this.#effectiveOffset())
    const pos = getSunPosition(utc, lat, lng)
    const deg = (r) => Math.round((r * 180) / Math.PI)
    if (this.hasAltitudeTarget) this.altitudeTarget.textContent = `${deg(pos.altitude)}°`
    if (this.hasAzimuthTarget) {
      const compass = ((deg(pos.azimuth) + 180) % 360 + 360) % 360
      this.azimuthTarget.textContent = `${compass}°`
    }
  }

  #syncWarning() {
    if (!this.hasWarningTarget) return
    const missing = !editorStore.plot?.originLngLat
    this.warningTarget.classList.toggle("hidden", !missing)
  }
}
